import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download, Search } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { useCurrency } from '../../hooks/useCurrency'
import type { PageMeta } from '../../lib/permissions/pageMeta'
import { PageHeader } from '../../components/ui/PageHeader'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { downloadCsv } from '../../lib/reportCsv'
import { displayDate, today } from '../../utils/dates'
import { cn } from '../../utils/cn'
import { methodLabel } from '../../lib/paymentReport/methodLabels'

// Sales Payment Report — customer receipts (order_payments, order_type='sale') over
// a period, read from the additive read-only RPC get_sales_payment_report (migs
// 387/388). Everything below — table, KPI tiles, per-method summary, top customers,
// daily trend, cashier breakdown, CSV — respects the period AND the method filter
// together. Reference = the receipt's invoice number; received_by = the staff who
// recorded it.

export const pageMeta: PageMeta = {
  key: 'trading.sales_payments',
  label: 'Sales Payments',
  module: 'Trading',
  section: 'Reports',
  description: 'Customer receipts (payments received) in the selected period',
  defaultPermissions: {
    owner: 'view', admin: 'view', manager: 'view',
    accountant: 'none', cashier: 'none', viewer: 'none',
  },
}

type Row = {
  reference: string
  paid_at: string
  customer_name: string
  method: string
  amount: number
  received_by?: string
}
type Totals = { amount: number; count: number; by_method: Record<string, number> }
type Report = { rows: Row[]; totals: Totals; from: string; to: string }

type Preset = 'today' | 'week' | 'month' | 'half' | 'year' | 'custom'
type SortKey = 'paid_at' | 'amount' | 'method'
const ALL = '__all__'

const PRESETS: { value: Preset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'half', label: 'This half-year' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom' },
]

// A safe file-name token for a selected method.
function methodSlug(m: string): string {
  return m.replace(/[^a-z0-9]+/gi, '-').toLowerCase()
}

function pad(n: number): string { return String(n).padStart(2, '0') }

// Resolve a preset to {from,to} in tenant-local time — the browser clock, WAT for
// the Nigerian user base, matching every other pill report. Relative presets run
// start-of-period → today (period-to-date).
function resolveRange(p: Preset, customFrom: string, customTo: string): { from: string; to: string } {
  const now = new Date()
  const y = now.getFullYear()
  const mo = now.getMonth()
  const d = now.getDate()
  const t = today()
  switch (p) {
    case 'today':
      return { from: t, to: t }
    case 'week': {
      const day = now.getDay()
      const back = (day + 6) % 7
      const mon = new Date(y, mo, d - back)
      return { from: `${mon.getFullYear()}-${pad(mon.getMonth() + 1)}-${pad(mon.getDate())}`, to: t }
    }
    case 'month':
      return { from: `${y}-${pad(mo + 1)}-01`, to: t }
    case 'half':
      return { from: mo < 6 ? `${y}-01-01` : `${y}-07-01`, to: t }
    case 'year':
      return { from: `${y}-01-01`, to: t }
    case 'custom': {
      const from = customFrom || customTo || t
      const to = customTo || customFrom || t
      return { from, to }
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (fn: string, args: Record<string, unknown>) => (supabase as any).rpc(fn, args)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

export default function SalesPaymentReportPage() {
  const { tenant } = useAuthStore()
  const { format } = useCurrency()
  const tenantId = tenant?.id ?? ''

  const [preset, setPreset] = useState<Preset>('month')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [search, setSearch] = useState('')
  const [method, setMethod] = useState<string>(ALL)
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'paid_at', dir: 'desc' })

  const range = useMemo(() => resolveRange(preset, customFrom, customTo), [preset, customFrom, customTo])

  const { data, isLoading, isError } = useQuery<Report>({
    queryKey: ['sales-payment-report', tenantId, range.from, range.to],
    enabled: !!tenantId && !!range.from && !!range.to,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await rpc('get_sales_payment_report', {
        p_tenant_id: tenantId, p_from: range.from, p_to: range.to,
      })
      if (error) throw error
      return data as Report
    },
  })

  const allRows = data?.rows ?? []

  // Distinct methods present in the (unfiltered) period — drives the filter selector.
  const methodOptions = useMemo(() => {
    const s = new Set(allRows.map(r => r.method))
    return Array.from(s).sort((a, b) => methodLabel(a).localeCompare(methodLabel(b)))
  }, [allRows])

  // Base scope = period rows narrowed to the active method. Everything (tiles,
  // summary, top customers, trend, cashier) is computed from this.
  const scopedRows = useMemo(
    () => (method === ALL ? allRows : allRows.filter(r => r.method === method)),
    [allRows, method],
  )

  // Visible table = scope + text search + sort.
  const tableRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = q
      ? scopedRows.filter(r =>
          r.customer_name.toLowerCase().includes(q) ||
          r.reference.toLowerCase().includes(q) ||
          methodLabel(r.method).toLowerCase().includes(q) ||
          r.method.toLowerCase().includes(q) ||
          (r.received_by ?? '').toLowerCase().includes(q))
      : scopedRows
    const sorted = [...filtered].sort((a, b) => {
      if (sort.key === 'amount') return sort.dir === 'desc' ? b.amount - a.amount : a.amount - b.amount
      if (sort.key === 'method') {
        const al = methodLabel(a.method), bl = methodLabel(b.method)
        const cmp = al < bl ? -1 : al > bl ? 1 : 0
        return sort.dir === 'desc' ? -cmp : cmp
      }
      const cmp = a.paid_at < b.paid_at ? -1 : a.paid_at > b.paid_at ? 1 : 0
      return sort.dir === 'desc' ? -cmp : cmp
    })
    return sorted
  }, [scopedRows, search, sort])

  // KPI tiles (scope).
  const kpis = useMemo(() => {
    const total = scopedRows.reduce((s, r) => s + r.amount, 0)
    const count = scopedRows.length
    const customers = new Set(scopedRows.map(r => r.customer_name)).size
    const avg = count ? total / count : 0
    return { total, count, customers, avg }
  }, [scopedRows])

  // Per-method summary (scope). When a method filter is active only that one shows.
  const perMethod = useMemo(() => {
    const m = new Map<string, { amount: number; count: number }>()
    for (const r of scopedRows) {
      const e = m.get(r.method) ?? { amount: 0, count: 0 }
      e.amount += r.amount; e.count += 1
      m.set(r.method, e)
    }
    const total = scopedRows.reduce((s, r) => s + r.amount, 0)
    return Array.from(m.entries())
      .map(([k, v]) => ({ method: k, ...v, share: total ? (v.amount / total) * 100 : 0 }))
      .sort((a, b) => b.amount - a.amount)
  }, [scopedRows])

  const topCustomers = useMemo(() => {
    const m = new Map<string, { amount: number; count: number }>()
    for (const r of scopedRows) {
      const e = m.get(r.customer_name) ?? { amount: 0, count: 0 }
      e.amount += r.amount; e.count += 1
      m.set(r.customer_name, e)
    }
    return Array.from(m.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5)
  }, [scopedRows])

  const cashiers = useMemo(() => {
    const m = new Map<string, { amount: number; count: number }>()
    for (const r of scopedRows) {
      const who = r.received_by ?? 'Unknown'
      const e = m.get(who) ?? { amount: 0, count: 0 }
      e.amount += r.amount; e.count += 1
      m.set(who, e)
    }
    return Array.from(m.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.amount - a.amount)
  }, [scopedRows])

  // Daily receipts (scope) — recessive sparkline, oldest → newest.
  const trend = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of scopedRows) m.set(r.paid_at, (m.get(r.paid_at) ?? 0) + r.amount)
    return Array.from(m.entries())
      .map(([day, amount]) => ({ day, amount }))
      .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0))
  }, [scopedRows])
  const trendMax = useMemo(() => Math.max(1, ...trend.map(d => d.amount)), [trend])

  // Resolve each receipt's invoice → sale id + customer id for deep links
  // (order_number is unique per tenant; chunked; no RPC change). Walk-in → no link.
  const refs = useMemo(
    () => Array.from(new Set(allRows.map(r => r.reference).filter(Boolean))),
    [allRows],
  )
  const { data: orderMap } = useQuery<Map<string, { id: string; customerId: string | null }>>({
    queryKey: ['sales-payment-order-map', tenantId, range.from, range.to],
    enabled: !!tenantId && refs.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const map = new Map<string, { id: string; customerId: string | null }>()
      for (let i = 0; i < refs.length; i += 200) {
        const chunk = refs.slice(i, i + 200)
        const { data: rows, error } = await db('sales_orders')
          .select('id, order_number, customer_id, customers(is_walkin)')
          .eq('tenant_id', tenantId)
          .in('order_number', chunk)
        if (error) throw error
        for (const r of (rows ?? []) as Array<{ id: string; order_number: string; customer_id: string | null; customers: { is_walkin: boolean | null } | null }>) {
          map.set(r.order_number, { id: r.id, customerId: r.customers?.is_walkin ? null : (r.customer_id ?? null) })
        }
      }
      return map
    },
  })

  const toggleSort = (key: SortKey) =>
    setSort(s => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }))

  const exportCsv = () => {
    if (!tableRows.length) return
    const suffix = `${range.from}_to_${range.to}` + (method === ALL ? '' : `-${methodSlug(method)}`)
    downloadCsv(
      `sales-payments-${suffix}`,
      ['Reference', 'Date', 'Customer', 'Payment method', 'Received by', 'Amount'],
      tableRows.map(r => [r.reference, r.paid_at, r.customer_name, methodLabel(r.method), r.received_by ?? '', r.amount]),
    )
  }

  const money = (n: number) => format(n)

  return (
    <div className="space-y-5">
      <PageHeader title="Sales Payments" subtitle="Customer receipts received in the selected period." />

      {/* Controls */}
      <div className="card p-4 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1">
            {PRESETS.map(p => (
              <button
                key={p.value} type="button" onClick={() => setPreset(p.value)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  preset === p.value ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                )}
              >{p.label}</button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" className="input py-1.5 w-auto" aria-label="From date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
              <span className="text-gray-400 text-sm">to</span>
              <input type="date" className="input py-1.5 w-auto" aria-label="To date" value={customTo} onChange={e => setCustomTo(e.target.value)} />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                className="input pl-9 py-1.5"
                placeholder="Search customer, reference, method or cashier…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select className="input py-1.5 w-auto" aria-label="Payment method filter" value={method} onChange={e => setMethod(e.target.value)}>
              <option value={ALL}>All methods</option>
              {methodOptions.map(m => <option key={m} value={m}>{methodLabel(m)}</option>)}
            </select>
          </div>
          <button
            type="button" onClick={exportCsv} disabled={!tableRows.length}
            className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : isError ? (
        <EmptyState title="Couldn't load the report" description="Please try again in a moment." />
      ) : !allRows.length ? (
        <EmptyState title="No payments in this period" description="Pick a different date range to see customer receipts." />
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Total received" value={money(kpis.total)} />
            <Tile label="Payments" value={kpis.count.toLocaleString()} />
            <Tile label="Paying customers" value={kpis.customers.toLocaleString()} />
            <Tile label="Average payment" value={money(kpis.avg)} />
          </div>

          {/* Daily trend — recessive sparkline */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Daily receipts</h3>
              <span className="text-xs text-gray-400">{trend.length} day{trend.length === 1 ? '' : 's'}</span>
            </div>
            {trend.length ? (
              <div className="flex items-end gap-0.5 h-14 overflow-x-auto">
                {trend.map(d => (
                  <div
                    key={d.day}
                    className="flex-1 min-w-[3px] rounded-t bg-brand-400/70 hover:bg-brand-500 transition-colors"
                    style={{ height: `${Math.max(2, (d.amount / trendMax) * 100)}%` }}
                    title={`${displayDate(d.day)} — ${money(d.amount)}`}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No receipts.</p>
            )}
          </div>

          {/* Summary cards: per-method, top customers, cashiers */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <MiniCard title="By payment method">
              {perMethod.map(m => (
                <MiniRow key={m.method} label={methodLabel(m.method)} sub={`${m.count} · ${m.share.toFixed(1)}%`} value={money(m.amount)} />
              ))}
            </MiniCard>
            <MiniCard title="Top customers">
              {topCustomers.length
                ? topCustomers.map(c => (
                    <MiniRow key={c.name} label={c.name} sub={`${c.count} payment${c.count === 1 ? '' : 's'}`} value={money(c.amount)} />
                  ))
                : <p className="text-sm text-gray-400">No customers.</p>}
            </MiniCard>
            <MiniCard title="Received by (cashier)">
              {cashiers.map(c => (
                <MiniRow key={c.name} label={c.name} sub={`${c.count} payment${c.count === 1 ? '' : 's'}`} value={money(c.amount)} />
              ))}
            </MiniCard>
          </div>

          {/* Detail table */}
          {!tableRows.length ? (
            <EmptyState title="No matching payments" description="Adjust the search or method filter." />
          ) : (
            <div className="card p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 border-b border-gray-200">
                      <th className="px-3 py-2 text-left font-medium">Reference</th>
                      <SortableTh label="Date" k="paid_at" sort={sort} onSort={toggleSort} align="left" />
                      <th className="px-3 py-2 text-left font-medium">Customer</th>
                      <SortableTh label="Payment method" k="method" sort={sort} onSort={toggleSort} align="left" />
                      <th className="px-3 py-2 text-left font-medium">Received by</th>
                      <SortableTh label="Amount" k="amount" sort={sort} onSort={toggleSort} align="right" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tableRows.map((r, i) => {
                      const link = r.reference ? orderMap?.get(r.reference) : undefined
                      return (
                        <tr key={`${r.reference}-${r.paid_at}-${i}`} className="hover:bg-gray-50">
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            {link
                              ? <Link to={`/sales/${link.id}`} className="text-brand-600 hover:underline">{r.reference}</Link>
                              : <span className="text-gray-500">{r.reference || '—'}</span>}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">{displayDate(r.paid_at)}</td>
                          <td className="px-3 py-2.5">
                            {link?.customerId
                              ? <Link to={`/contacts/customers/${link.customerId}/ledger`} className="text-brand-600 hover:underline">{r.customer_name}</Link>
                              : r.customer_name}
                          </td>
                          <td className="px-3 py-2.5">{methodLabel(r.method)}</td>
                          <td className="px-3 py-2.5 text-gray-600">{r.received_by ?? '—'}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums font-medium">{money(r.amount)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 font-semibold text-gray-900 bg-gray-50/60">
                      <td className="px-3 py-3" colSpan={5}>
                        Total — {tableRows.length.toLocaleString()} payment{tableRows.length === 1 ? '' : 's'}
                        {method !== ALL && <span className="ml-1 font-normal text-gray-500">({methodLabel(method)})</span>}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {money(tableRows.reduce((s, r) => s + r.amount, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">{value}</p>
    </div>
  )
}

function MiniCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  )
}

function MiniRow({ label, sub, value }: { label: string; sub: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <div className="min-w-0">
        <div className="truncate text-gray-800">{label}</div>
        <div className="text-xs text-gray-400">{sub}</div>
      </div>
      <div className="tabular-nums font-medium text-gray-900 shrink-0">{value}</div>
    </div>
  )
}

function SortableTh({
  label, k, sort, onSort, align,
}: {
  label: string
  k: SortKey
  sort: { key: SortKey; dir: 'asc' | 'desc' }
  onSort: (k: SortKey) => void
  align: 'left' | 'right'
}) {
  const active = sort.key === k
  return (
    <th className={cn('px-3 py-2 font-medium', align === 'right' ? 'text-right' : 'text-left')}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={cn('inline-flex items-center gap-1 hover:text-gray-900', active && 'text-gray-900')}
      >
        {label}
        {active && <span className="text-[10px]">{sort.dir === 'desc' ? '▼' : '▲'}</span>}
      </button>
    </th>
  )
}
